/**
 * Shared SplitContainer properties and their parse for the HSplit/VSplit slices, in pure `.ts` that
 * both parsers can import inside the linter graph. `splitContainerSolver.ts` lays the children out.
 */
import { parseOptionalBool, parseOptionalInt } from '../../../../parser/valueParsers';
import { packedArrayBody, packedArrayForms } from '../../../../godot/variant/variantParser.js';
import { slotInt } from '../../../../godot/variant/intSlots.js';
import { parseControl } from '../control/parser';
/** All three spellings a PACKED_INT32_ARRAY slot converts (`godot/variant/variantParser.ts`). */
const SPLIT_OFFSETS_FORMS = packedArrayForms('PackedInt32Array');
/**
 * `split_offsets` and the deprecated `split_offset`, resolved as the engine does: the constructor
 * seeds `[0]` (`split_container.cpp:1336`), `set_split_offsets` replaces the array (`:1071-1077`)
 * and `set_split_offset` writes entry 0 (`:1056-1064`, from the compat property at `:1330`). The
 * saver writes `split_offsets` first (`:1295`), so file order decides and the bag is read in order.
 */
function parseSplitOffsets(properties) {
    let offsets;
    for (const key of Object.keys(properties)) {
        if (key === 'split_offsets') {
            const matched = packedArrayBody(SPLIT_OFFSETS_FORMS, properties['split_offsets']);
            if (!matched)
                continue;
            offsets = matched.body
                .split(',')
                .map((element) => element.trim())
                .filter((element) => element.length > 0)
                .map((element) => slotInt(element) ?? 0);
        }
        else if (key === 'split_offset') {
            const scalar = parseOptionalInt(properties['split_offset']);
            if (scalar === undefined)
                continue;
            const base = offsets ?? [0];
            // `set_split_offset`'s `ERR_FAIL_INDEX(0, split_offsets.size())`: an explicitly empty array
            // has no entry 0, so it swallows the scalar.
            if (base.length === 0)
                continue;
            offsets = [...base];
            offsets[0] = scalar;
        }
    }
    return offsets;
}
export function parseSplitContainer(heading, properties) {
    const result = { ...parseControl(heading, properties) };
    result.splitOffset = parseOptionalInt(properties.split_offset);
    result.splitOffsets = parseSplitOffsets(properties);
    result.collapsed = parseOptionalBool(properties.collapsed);
    result.draggerVisibility = parseOptionalInt(properties.dragger_visibility);
    return result;
}
/** This container's authored offsets, or the constructor's own single zero (`split_container.cpp:1336`). */
export function splitOffsetsOf(props) {
    return props.splitOffsets ?? [props.splitOffset ?? 0];
}
//# sourceMappingURL=splitContainer.js.map